import { useEffect } from "react";
import axios from "../../api/axios";
import { useDispatch } from "react-redux";
import { logoutUserWithoutToken } from "../../slices/session";
import i18n from "../../../i18n";
import { buildLoginPath } from "../../utils/authReturnUrl";

const STYLE_ID = "session-expired-styles";
const MODAL_ID = "session-expired-modal";

const ensureStyles = () => {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    #${MODAL_ID} { font-family: 'Inter', system-ui, -apple-system, sans-serif; }
    #${MODAL_ID} .se-scrim { position:absolute; inset:0; background:rgba(7,11,22,.72); backdrop-filter:blur(3px); -webkit-backdrop-filter:blur(3px); }
    #${MODAL_ID} .se-card { position:relative; width:100%; max-width:430px; margin:24px; background:#ffffff; border-radius:20px; box-shadow:0 30px 70px -20px rgba(2,6,18,.7), 0 0 0 1px rgba(255,255,255,.06); overflow:hidden; animation:sePop .22s ease-out; }
    #${MODAL_ID} .se-header { padding:18px 26px 0; }
    #${MODAL_ID} .se-logo { font-family:'Inter Tight','Inter',sans-serif; font-weight:700; font-size:19px; letter-spacing:-.02em; }
    #${MODAL_ID} .se-logo-pro { color:#1463C7; }
    #${MODAL_ID} .se-logo-mang { color:#1e293b; }
    #${MODAL_ID} .se-body { padding:24px 36px 34px; text-align:center; }
    #${MODAL_ID} .se-icon { position:relative; width:72px; height:72px; margin:6px auto 22px; }
    #${MODAL_ID} .se-icon-ring { position:absolute; inset:0; border-radius:50%; background:#EAF3FD; animation:seRing 2.4s ease-out infinite; }
    #${MODAL_ID} .se-icon-disc { position:relative; width:72px; height:72px; border-radius:50%; background:#EAF3FD; display:flex; align-items:center; justify-content:center; }
    #${MODAL_ID} .se-title { margin:0 0 10px; font-family:'Inter Tight','Inter',sans-serif; font-weight:700; font-size:24px; letter-spacing:-.02em; color:#0f172a; }
    #${MODAL_ID} .se-text { margin:0 auto; font-size:15px; line-height:1.55; color:#64748B; max-width:320px; }
    #${MODAL_ID} .se-actions { display:flex; flex-direction:column; gap:10px; margin-top:28px; }
    #${MODAL_ID} .se-btn-primary { appearance:none; border:none; cursor:pointer; width:100%; height:48px; border-radius:12px; background:linear-gradient(135deg,#1463C7,#2D8BE8); color:#fff; font-family:inherit; font-weight:700; font-size:15px; letter-spacing:.01em; box-shadow:0 12px 24px -8px rgba(20,99,199,.6); transition:transform .12s ease, box-shadow .2s ease, filter .2s ease; }
    #${MODAL_ID} .se-btn-primary:hover { filter:brightness(1.04); box-shadow:0 14px 28px -8px rgba(20,99,199,.65); }
    #${MODAL_ID} .se-btn-primary:active { transform:translateY(1px); }
    #${MODAL_ID} .se-btn-secondary { appearance:none; border:none; cursor:pointer; width:100%; height:46px; border-radius:12px; background:transparent; color:#64748b; font-family:inherit; font-weight:600; font-size:14.5px; transition:background .15s ease, color .15s ease; }
    #${MODAL_ID} .se-btn-secondary:hover { background:#f1f4f9; color:#334155; }
    @keyframes seRing { 0%{transform:scale(1);opacity:.55} 70%{transform:scale(1.6);opacity:0} 100%{transform:scale(1.6);opacity:0} }
    @keyframes sePop { from{opacity:0;transform:translateY(8px) scale(.98)} to{opacity:1;transform:none} }
    @media (prefers-reduced-motion: reduce){ #${MODAL_ID} .se-icon-ring{animation:none} #${MODAL_ID} .se-card{animation:none} }
  `;
  document.head.appendChild(style);
};

const removeModal = () => {
  const modal = document.getElementById(MODAL_ID);
  if (modal && modal.parentNode) {
    modal.parentNode.removeChild(modal);
  }
};

const SessionExpiredModal = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    const handleOk = () => {
      dispatch(logoutUserWithoutToken());
      const returnPath = `${window.location.pathname}${window.location.search}`;
      removeModal();
      window.location.href = buildLoginPath(returnPath);
    };

    const handleCancel = () => {
      removeModal();
    };

    const showModal = () => {
      if (document.getElementById(MODAL_ID)) return;
      ensureStyles();

      const modal = document.createElement("div");
      modal.id = MODAL_ID;
      modal.classList.add(
        "fixed",
        "inset-0",
        "z-50",
        "flex",
        "items-center",
        "justify-center"
      );
      modal.innerHTML = `
        <div class="se-scrim"></div>
        <div role="dialog" aria-modal="true" aria-labelledby="se-title" class="se-card">
          <div class="se-header">
            <span class="se-logo"><span class="se-logo-pro">Pro</span><span class="se-logo-mang">mang</span></span>
          </div>
          <div class="se-body">
            <div class="se-icon">
              <span class="se-icon-ring"></span>
              <div class="se-icon-disc">
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#1463C7" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="9"></circle>
                  <path d="M12 7.5V12l3 1.8"></path>
                </svg>
              </div>
            </div>
            <h2 id="se-title" class="se-title">${i18n.t("auth.session_expired.title")}</h2>
            <p class="se-text">${i18n.t("auth.session_expired.message")}</p>
            <div class="se-actions">
              <button type="button" class="se-btn-primary" id="logoutBtn">${i18n.t("auth.session_expired.login")}</button>
              <button type="button" class="se-btn-secondary" id="cancelBtn">${i18n.t("auth.session_expired.cancel")}</button>
            </div>
          </div>
        </div>
      `;
      document.body.appendChild(modal);
      modal.querySelector("#logoutBtn").addEventListener("click", handleOk);
      modal.querySelector("#cancelBtn").addEventListener("click", handleCancel);
    };

    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401) {
          console.error("Session expired (401):", error);
        } else if (error?.response) {
          console.error(`API error (${error.response.status}):`, error);
        } else {
          console.error("Request error:", error);
        }

        if (
          error?.response?.status === 401 &&
          window.location.pathname !== "/authentication/login"
        ) {
          showModal();
        }

        if (error?.response) {
          return Promise.resolve(error.response);
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [dispatch]);

  return null;
};

export default SessionExpiredModal;
